import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';
import DateInput from '../components/DateInput';
import { logPolicyAction } from '../utils/policyLogger';
import { usePermissions } from '../context/PermissionsContext';

interface PolicyFormData {
  policy_number: string;
  insurer: string;
  status: 'active' | 'cancelled';
  start_date: Date | null;
  end_date: Date | null;
  renewal_date: Date | null;
  premium_amount: string;
  cancellation_date: Date | null;
  cancellation_reason: string;
}

const CreatePolicy = () => {
  const { can } = usePermissions();
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [client, setClient] = useState<any>(null);

  // Get prefill data from navigation state
  const prefillData = (location.state as any)?.prefillData || {};

    // Generate a unique policy number
  const generateUniquePolicyNumber = () => {
    const prefix = 'POL';
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    return `${prefix}-${timestamp}-${random}`;
  };

const [formData, setFormData] = useState<PolicyFormData>({
  policy_number: prefillData.policy_number ? `${prefillData.policy_number}-POL` : generateUniquePolicyNumber(),
  insurer: prefillData.insurer || '',
  status: 'active',
  start_date: new Date(),
  end_date: null,
  renewal_date: null,
  premium_amount: prefillData.premium_amount || '',
  cancellation_date: null,
  cancellation_reason: ''
});

  useEffect(() => {
    fetchUserAndClient();
  }, [clientId]);

  const fetchUserAndClient = async () => {
    try {
        const token = localStorage.getItem('token');
        if (!token) {
        navigate('/login');
        return;
        }

        // Get user from localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
        setUser(JSON.parse(storedUser));
        }

        // Fetch client using the correct endpoint
        if (clientId) {
        const clientRes = await axios.get(`${process.env.REACT_APP_API_URL}/clients/${clientId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        setClient(clientRes.data);
        }
    } catch (err) {
        console.error('Failed to fetch client:', err);
        // Set minimal client object so header still works
        if (clientId) {
        setClient({ id: clientId, client_number: clientId });
        }
    }
    };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date: Date | null, field: keyof PolicyFormData) => {
    setFormData(prev => ({ ...prev, [field]: date }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setLoading(true);
  setError(null);

  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }

    if (!clientId) {
      throw new Error('Client ID is required');
    }

    // Get the quote ID from prefill data
    const quoteId = prefillData.quoteId;

    // DEBUG: Log the quoteId
    console.log('🔍 CreatePolicy - prefillData:', prefillData);
    console.log('🔍 CreatePolicy - quoteId:', quoteId);

    const payload = {
      clientId: parseInt(clientId),
      policy_number: formData.policy_number,
      product_name: 'Policy',
      insurer: formData.insurer || null,
      status: formData.status,
      start_date: formData.start_date ? formData.start_date.toISOString().split('T')[0] : null,
      end_date: formData.end_date ? formData.end_date.toISOString().split('T')[0] : null,
      renewal_date: formData.renewal_date ? formData.renewal_date.toISOString().split('T')[0] : null,
      premium_amount: parseFloat(formData.premium_amount),
      cancellation_date: formData.cancellation_date ? formData.cancellation_date.toISOString().split('T')[0] : null,
      cancellation_reason: formData.cancellation_reason || null,
      quote_id: quoteId  // ✅ Add the quote ID to the payload
    };

    console.log('📝 Creating policy with quote_id:', quoteId);

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/policies`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    console.log('✅ Policy created response:', response.data);

    // Log the policy creation
    if (response.data.data && response.data.data.id) {
      await logPolicyAction(
        response.data.data.id,
        'CREATED',
        `Policy ${formData.policy_number} created from quote ${quoteId}`,
        {
          policy_number: formData.policy_number,
          insurer: formData.insurer,
          premium_amount: formData.premium_amount,
          start_date: formData.start_date?.toISOString().split('T')[0],
          quote_id: quoteId
        }
      );
    }

    // Navigate back to client page with policies tab active
    navigate(`/personal/${clientId}`, { 
      state: { activeTab: 'policies' }
    });
  } catch (err: any) {
    console.error('Failed to create policy:', err);
    setError(err.response?.data?.message || 'Failed to create policy');
  } finally {
    setLoading(false);
  }
};

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

    if (!can('policies', 'write')) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">You do not have permission to create Policies.</p>
      </div>
    );
  }


  if (!user) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title="Create New Policy"
          user={user}
          client={client}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={!!client}
        />
        <div className="flex-1 p-6">
          <div className="bg-white rounded-lg shadow-md p-6 max-w-3xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Create New Policy</h2>
              <button
                onClick={() => navigate(`/personal/${clientId}`, { state: { activeTab: 'policies' } })}
                className="text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Policy Number *
                  </label>
                  <input
                    type="text"
                    name="policy_number"
                    value={formData.policy_number}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="POL-001"
                  />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Insurer (Administrator/Underwriter)
                    </label>
                    <input
                        type="text"
                        name="insurer"
                        value={formData.insurer}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Momentum, Discovery, Old Mutual"
                    />
                    </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status *
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Premium Amount (ZAR) *
                  </label>
                  <input
                    type="number"
                    name="premium_amount"
                    value={formData.premium_amount}
                    onChange={handleInputChange}
                    required
                    min="0.01"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="500.00"
                  />
                </div>

                <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Start Date *
  </label>
  <DateInput
    value={formData.start_date ? formData.start_date.toISOString().split('T')[0] : ''}
    onChange={(date) => {
      if (date) {
        const parsedDate = new Date(date);
        handleDateChange(parsedDate, 'start_date');
      } else {
        handleDateChange(null, 'start_date');
      }
    }}
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
    placeholder="Select start date"
  />
</div>

                <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    End Date
  </label>
  <DateInput
    value={formData.end_date ? formData.end_date.toISOString().split('T')[0] : ''}
    onChange={(date) => {
      if (date) {
        const parsedDate = new Date(date);
        handleDateChange(parsedDate, 'end_date');
      } else {
        handleDateChange(null, 'end_date');
      }
    }}
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
    placeholder="Select end date (optional)"
  />
</div>

                <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Renewal Date
  </label>
  <DateInput
    value={formData.renewal_date ? formData.renewal_date.toISOString().split('T')[0] : ''}
    onChange={(date) => {
      if (date) {
        const parsedDate = new Date(date);
        handleDateChange(parsedDate, 'renewal_date');
      } else {
        handleDateChange(null, 'renewal_date');
      }
    }}
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
    placeholder="Select renewal date (optional)"
  />
</div>

                {formData.status === 'cancelled' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cancellation Date *
                      </label>
                      <DateInput
                        value={formData.cancellation_date ? formData.cancellation_date.toISOString().split('T')[0] : ''}
                        onChange={(date) => {
                          if (date) {
                            const parsedDate = new Date(date);
                            handleDateChange(parsedDate, 'cancellation_date');
                          } else {
                            handleDateChange(null, 'cancellation_date');
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Select cancellation date"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cancellation Reason
                      </label>
                      <textarea
                        name="cancellation_reason"
                        value={formData.cancellation_reason}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Reason for cancellation..."
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => navigate(`/personal/${clientId}`, { state: { activeTab: 'policies' } })}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create Policy'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePolicy;